/**
 * MapLibre's worker, which the bundler has no reason to ship and therefore did not.
 *
 * MapLibre GL 6 stopped inlining its worker as a blob. It now derives the URL from the bundle's
 * own location:
 *
 * ```js
 * new URL('./maplibre-gl-worker.mjs', import.meta.url)
 * ```
 *
 * That file sits in `maplibre-gl/dist`, and nothing in this project imports it, so Vite never
 * emitted it. The built app asked for `/assets/maplibre-gl-worker.mjs`, got a 404, and handed
 * that URL to `new Worker()` anyway. **Nothing throws.** MapLibre does not listen for `error`
 * on the worker, so the failure is completely silent: the actor simply never answers, and its
 * pending callbacks pile up forever.
 *
 * The damage is much wider than it looks, because it is not only vector tiles that are parsed
 * off the main thread — **GeoJSON is too**. So the symptom set was:
 *
 *   * the vector basemap never drew a single pixel, and never requested one tile;
 *   * the airspace vector tiles were missing;
 *   * the flight track, the wall, the ground line and the marker were all absent — on the
 *     satellite map as well, which otherwise looked perfectly healthy;
 *   * satellite imagery, the orthophotos and the terrain DEM worked throughout, because raster
 *     is decoded on the main thread and never touches the worker;
 *   * and the console was empty.
 *
 * Which is exactly the combination that made this look like four unrelated bugs.
 *
 * `?worker&url` and not the obvious `?url`: the shipped worker is only an 18 kB shim that does
 * `import './maplibre-gl-shared.mjs'` for the other 480 kB. `?url` copies the one file
 * verbatim, so the 404 simply moves down a level and the worker still never boots. `?worker`
 * runs it through Vite's own worker build, which follows that import and emits one
 * self-contained, fingerprinted module — fingerprinted being the part that matters, because the
 * daemon serves `/assets/*` as `immutable` and may only do so if every name carries a hash.
 */

import { setWorkerUrl } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

setWorkerUrl(workerUrl);

/**
 * Whether the worker is actually alive, for the map components to show rather than hang.
 *
 * A round trip through the worker pool is the only honest test — the URL being fetchable does
 * not prove the worker booted. This resolves false rather than rejecting, because the caller
 * wants a message on the screen, not an exception.
 */
export async function workerIsAlive(timeoutMs = 8000): Promise<boolean> {
  try {
    const response = await Promise.race([
      fetch(workerUrl, { method: 'GET' }).then((r) => r.ok),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
    ]);
    return response;
  } catch {
    return false;
  }
}

export { workerUrl };

/**
 * Collapse the attribution control, which MapLibre deliberately opens on first render.
 *
 * `compact: true` only promises a *collapsible* control, not a collapsed one: on the first
 * update `_updateCompact` adds `maplibregl-compact` **and** `maplibregl-compact-show`
 * together, so the full licence text — two providers, four URLs, about 160 characters — sits
 * across the bottom of the map until somebody clicks the ⓘ. On a map that is the whole point of
 * the page, that is a banner, not an attribution.
 *
 * The one thing that makes this stable rather than a fight with the library: MapLibre only adds
 * the pair when `maplibregl-compact` is **not already present**. Setting that class ourselves,
 * once, therefore takes the branch out of play for good — every later data event leaves the
 * control alone, and the ⓘ toggle keeps working exactly as it should.
 *
 * The attribution itself is not hidden, and must not be: LGL-BW and EOX both require it. It is
 * one click away and still in the DOM, which is what the licences ask for.
 */
export function collapseAttribution(map: {
  getContainer(): HTMLElement;
  once(type: 'idle', listener: () => void): unknown;
}): void {
  const collapse = () => {
    const el = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
    if (el === null) return;
    el.classList.add('maplibregl-compact');
    el.classList.remove('maplibregl-compact-show');
    el.removeAttribute('open');
  };
  collapse();
  // Again once the map settles: on a cold load the control can be created empty, and an empty
  // one carries `maplibregl-attrib-empty`, which makes MapLibre skip the branch above and take
  // it later instead — after the first tiles arrive with their attribution.
  map.once('idle', collapse);
}
